import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import axios from 'axios';
import { toast } from 'react-toastify';
import { Scale, MessageSquare, Send, Check } from 'lucide-react';
import './VerdictChips.css';

const VERDICT_OPTIONS = [
  { id: 'GUILTY', label: 'Guilty', icon: '⚖️', color: '#ef4444' },
  { id: 'INNOCENT', label: 'Innocent', icon: '🛡️', color: '#10b981' },
  { id: 'CLIENT_IS_A_CLOWN', label: 'Client is a Clown', icon: '🤡', color: '#f59e0b' },
  { id: 'STRAIGHT_TO_JAIL', label: 'Straight to Jail', icon: '⛓️', color: '#8b5cf6' },
  { id: 'JUSTIFIED', label: 'Justified Crime', icon: '🔥', color: '#3b82f6' },
  { id: 'SETTLE_OUT_OF_COURT', label: 'Settle Out of Court', icon: '🤝', color: '#6b7280' }
];

const VerdictChips = ({ caseId, verdictCounts = {}, totalVotes = 0, onVoteSuccess }) => {
  const { currentUser, getIdToken, userData } = useAuth();
  const [selectedVerdict, setSelectedVerdict] = useState(null);
  const [roastComment, setRoastComment] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasVoted, setHasVoted] = useState(false);

  const handleVoteSubmit = async (e) => {
    e.preventDefault();
    if (!selectedVerdict) {
      toast.warn('Select a verdict before submitting to the Jury!');
      return;
    }

    try {
      setIsSubmitting(true);
      const token = await getIdToken();
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';

      const res = await axios.post(
        `${apiUrl}/api/court/vote-verdict`,
        {
          caseId,
          verdictChip: selectedVerdict,
          roastComment,
          authorAlias: userData?.name || currentUser?.displayName || 'Juror'
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      toast.success(res.data.message || 'Verdict rendered! +15 Juror points.');
      setHasVoted(true);
      if (onVoteSuccess) onVoteSuccess();
    } catch (err) {
      console.error('Error casting verdict:', err);
      toast.error(err.response?.data?.error || 'Failed to submit jury verdict.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const calculatedTotal = totalVotes || Object.values(verdictCounts).reduce((a, b) => a + b, 0);

  return (
    <div className="verdict-jury-container">
      <div className="verdict-header">
        <div className="verdict-title-box">
          <Scale className="court-scale-icon" size={20} />
          <h3>Jury Box Deliberation</h3>
        </div>
        <span className="total-jurors-count">{calculatedTotal} Juror Votes Recorded</span>
      </div>

      {/* Live Verdict Tally Bar */}
      <div className="verdict-tallies-grid">
        {VERDICT_OPTIONS.map(opt => {
          const count = verdictCounts[opt.id] || 0;
          const pct = calculatedTotal > 0 ? Math.round((count / calculatedTotal) * 100) : 0;
          const isSelected = selectedVerdict === opt.id;

          return (
            <button
              key={opt.id}
              type="button"
              className={`verdict-chip-card ${isSelected ? 'selected' : ''}`}
              style={{ '--accent-color': opt.color }}
              onClick={() => setSelectedVerdict(opt.id)}
            >
              <div className="chip-top">
                <span className="chip-icon">{opt.icon}</span>
                <span className="chip-label">{opt.label}</span>
              </div>
              <div className="chip-progress-bar">
                <div className="chip-progress-fill" style={{ width: `${pct}%`, background: opt.color }} />
              </div>
              <div className="chip-stats">
                <span className="chip-count">{count} votes</span>
                <span className="chip-pct">{pct}%</span>
              </div>
            </button>
          );
        })}
      </div>

      {/* Roast & Verdict Submission Form */}
      {!hasVoted ? (
        <form onSubmit={handleVoteSubmit} className="jury-roast-form">
          <div className="form-label-row">
            <label>
              <MessageSquare size={16} />
              Deliver your Jury Roast or Legal Argument (Optional):
            </label>
            {selectedVerdict && (
              <span className="active-selected-tag">
                Voting: <strong>{selectedVerdict.replace(/_/g, ' ')}</strong>
              </span>
            )}
          </div>
          <textarea
            value={roastComment}
            onChange={(e) => setRoastComment(e.target.value)}
            placeholder="Explain why this client is unhinged, or roast the situation for the court record..."
            rows={3}
          />
          <button
            type="submit"
            className="btn-cast-verdict"
            disabled={!selectedVerdict || isSubmitting}
          >
            <Send size={16} />
            {isSubmitting ? 'Recording Verdict...' : 'Cast Gavel Vote (+15 Pts)'}
          </button>
        </form>
      ) : (
        <div className="vote-success-banner">
          <Check size={18} />
          <span>Your jury deliberation has been recorded. The Court thanks you for your service!</span>
        </div>
      )}
    </div>
  );
};

export default VerdictChips;
